import { motion } from 'framer-motion';
import { useEffect,useState } from 'react';
import { Link } from 'react-router';

import { strapiService } from '../../services/strapiService';
import type { Article } from '../../types/article';
import OptimizedImage from '../OptimizedImage';

const BlogPage = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const data = await strapiService.getArticles();
        setArticles(data);
        setError(null);
      } catch (err) {
        setError('Failed to load articles. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface section">
        <div className="container-custom">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface section">
        <div className="container-custom">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <p className="text-theme-secondary text-lg mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="section">
        <div className="container-custom">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-primary dark:text-theme-primary mb-4">
              featured wisdom
            </h1>
            <div className="w-24 h-1 bg-brand-secondary mx-auto mb-6"></div>
            <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
              explore a collection of articles written by konnect coaches and thought leaders to help you grow deeper relationships
            </p>
          </motion.div>

          {/* Articles Grid */}
          {articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-theme-secondary text-lg">No articles found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-card rounded-xl shadow-surface overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  <Link to={`/wisdom/${article.slug}`} className="block">
                    {/* Cover Image */}
                    <div className="relative h-48 overflow-hidden bg-panel">
                      {article.cover ? (
                        <OptimizedImage
                          src={strapiService.getMediaUrl(article.cover.url)}
                          alt={article.cover.alternativeText || article.title}
                          className="group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                      ) : (
                        <div className="w-full h-full bg-panel flex items-center justify-center">
                          <span className="text-theme-secondary">No image</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Title */}
                      <h2 className="text-xl font-bold text-primary dark:text-theme-primary mb-2 line-clamp-2 group-hover:text-brand-primary dark:group-hover:text-brand-secondary transition-colors">
                        {article.title}
                      </h2>

                      {/* Description */}
                      <p className="text-theme-secondary mb-4 line-clamp-3">
                        {article.description}
                      </p>

                      {/* Author & Date */}
                      <div className="flex items-center justify-between text-sm text-theme-secondary">
                        {article.author && (
                          <span className="font-medium">{article.author.name}</span>
                        )}
                        <time dateTime={article.publishedAt}>
                          {new Date(article.publishedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </time>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
